'use es6';

import { useMemo, useReducer, Fragment } from 'react';
import PropTypes from 'prop-types';
import { GYPSUM, LORAX, CALYPSO, KOALA } from 'HubStyleTokens/colors';
import styled, { css } from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Big from 'UIComponents/elements/Big';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITable from 'UIComponents/table/UITable';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIImage from 'UIComponents/image/UIImage';
import { getDataSyncsDataI18nKey } from '../../utils/i18n';
import DataSync from '../../records/DataSync';
import * as FieldTypes from '../../constants/dataSyncs/FieldType';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const textPath = 'ecosystemMarketplaceCore.dataSyncs';
const DataObject = styled(Big).withConfig({
  displayName: "DataSyncCard__DataObject",
  componentId: "sc-1e90h7y-0"
})(["text-transform:none;font-weight:500;"]);
const rowHeightStyles = css(["height:48px;td{height:48px !important;}"]);
const FieldMappingDropdownTR = styled.tr.withConfig({
  displayName: "DataSyncCard__FieldMappingDropdownTR",
  componentId: "sc-1e90h7y-1"
})(["background-color:", ";cursor:pointer;", ""], props => props.open ? GYPSUM : 'initial', rowHeightStyles);
const FieldMappingTR = styled.tr.withConfig({
  displayName: "DataSyncCard__FieldMappingTR",
  componentId: "sc-1e90h7y-2"
})(["&:hover{background-color:unset !important;}", ""], rowHeightStyles);
const FieldMappingsCaret = ({
  open
}) => /*#__PURE__*/_jsx(UIIcon, {
  className: "m-right-2",
  name: open ? 'down' : 'right',
  size: "xxs",
  color: CALYPSO
});
const initialState = {
  [FieldTypes.DEFAULT]: false,
  [FieldTypes.CUSTOM]: false
};
const reducer = (state, action) => Object.assign({}, state, {
  [action.type]: !state[action.type]
});

/*
  For Ops Hub listings only
 */
const DataSyncCard = ({
  dataSync,
  iconUrl,
  onToggleOpen,
  SyncIcon
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    sync,
    target,
    data
  } = dataSync;
  const renderedFieldMappings = useMemo(() => {
    return Object.values(FieldTypes).map(fieldType => {
      const mappings = dataSync.getFieldMappingsByType(fieldType);
      const dropdownOpen = state[fieldType];
      if (!mappings.size) {
        return null;
      }
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(FieldMappingDropdownTR, {
          open: dropdownOpen,
          onClick: () => {
            dispatch({
              type: fieldType
            });
            if (onToggleOpen) {
              onToggleOpen(fieldType, !dropdownOpen);
            }
          },
          children: /*#__PURE__*/_jsxs("td", {
            colSpan: 3,
            style: {
              fontWeight: 500
            },
            children: [/*#__PURE__*/_jsx(FieldMappingsCaret, {
              open: dropdownOpen
            }), /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${textPath}.fieldMappings.${fieldType}`
            })]
          })
        }), dropdownOpen && mappings.map((mapping, index) => {
          const isCustomMapping = mapping.fieldType === FieldTypes.CUSTOM;
          return /*#__PURE__*/_jsxs(FieldMappingTR, {
            children: [/*#__PURE__*/_jsx("td", {
              colSpan: isCustomMapping ? 3 : 1,
              children: mapping.appField
            }), !isCustomMapping && /*#__PURE__*/_jsxs(Fragment, {
              children: [/*#__PURE__*/_jsx("td", {}), /*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx("span", {
                  className: "m-left-4",
                  children: mapping.hubSpotField
                })
              })]
            })]
          }, `default-mapping-${index}`);
        })]
      }, fieldType);
    });
  }, [dataSync, state, onToggleOpen]);
  return /*#__PURE__*/_jsx(UICardWrapper, {
    style: {
      border: `1px solid ${KOALA}`
    },
    children: /*#__PURE__*/_jsxs(UITable, {
      condensed: true,
      children: [/*#__PURE__*/_jsx("thead", {
        children: /*#__PURE__*/_jsxs("tr", {
          style: {
            height: 60,
            backgroundColor: GYPSUM
          },
          children: [/*#__PURE__*/_jsx("th", {
            style: {
              width: '40%'
            },
            children: /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              children: [iconUrl && /*#__PURE__*/_jsx(UIImage, {
                src: iconUrl,
                width: 24,
                className: "m-right-2"
              }), /*#__PURE__*/_jsx(DataObject, {
                children: target
              })]
            })
          }), /*#__PURE__*/_jsx(UITooltip, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `ecosystemMarketplaceCore.dataSyncs.sync.${sync}`
            }),
            children: /*#__PURE__*/_jsx("th", {
              className: "p-top-3",
              style: {
                width: '10%'
              },
              children: /*#__PURE__*/_jsx(SyncIcon, {
                sync: sync
              })
            })
          }), /*#__PURE__*/_jsx("th", {
            style: {
              width: '50%'
            },
            children: /*#__PURE__*/_jsxs(UIFlex, {
              align: "center",
              children: [/*#__PURE__*/_jsx(UIIcon, {
                name: "sprocket",
                color: LORAX,
                size: "xs",
                className: "m-top-1 m-right-2 m-left-4"
              }), /*#__PURE__*/_jsx(DataObject, {
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: getDataSyncsDataI18nKey(data)
                })
              })]
            })
          })]
        })
      }), /*#__PURE__*/_jsx("tbody", {
        children: renderedFieldMappings
      })]
    })
  }, target);
};
DataSyncCard.propTypes = {
  dataSync: PropTypes.instanceOf(DataSync).isRequired,
  iconUrl: PropTypes.string,
  onToggleOpen: PropTypes.func,
  SyncIcon: PropTypes.elementType.isRequired
};
export default DataSyncCard;